import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  FileSpreadsheet,
  Link2,
  Unlink,
  Loader2,
  Cloud,
  Webhook,
  RefreshCw,
  Users,
  type LucideIcon,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type Category = "CRM" | "Storage" | "Spreadsheets" | "Webhooks";

interface IntegrationRow {
  key: string;
  name: string;
  category: Category;
  icon: LucideIcon;
  connected: boolean;
  detail?: string;
  /** Where to go to connect it */
  route: string;
  /** How to disconnect: table + row id (real delete, no fake success) */
  disconnect?: { table: "crm_integrations" | "google_tokens" | "microsoft_tokens" | "dropbox_tokens" | "crm_webhooks"; id: string };
}

const CATALOG: Array<{ key: string; name: string; category: Category; icon: LucideIcon; route: string }> = [
  { key: "hubspot", name: "HubSpot", category: "CRM", icon: Users, route: "/data" },
  { key: "zoho", name: "Zoho CRM", category: "CRM", icon: Users, route: "/data" },
  { key: "google_sheets", name: "Google Sheets", category: "Spreadsheets", icon: FileSpreadsheet, route: "/data" },
  { key: "excel_online", name: "Excel Online / OneDrive", category: "Spreadsheets", icon: FileSpreadsheet, route: "/data" },
  { key: "dropbox", name: "Dropbox", category: "Storage", icon: Cloud, route: "/data" },
];

const CATEGORY_ORDER: Category[] = ["CRM", "Spreadsheets", "Storage", "Webhooks"];

/**
 * Central hub for every external connection this account has.
 * Status is read from real rows (crm_integrations, *_tokens, crm_webhooks) and
 * disconnecting deletes the stored credential — never a cosmetic toast.
 */
export const IntegrationSettings = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<IntegrationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setRows([]); return; }

      const [crm, google, microsoft, dropbox, webhooks] = await Promise.all([
        supabase.from("crm_integrations").select("id, name, crm_type, status, last_sync_at").eq("user_id", user.id),
        supabase.from("google_tokens").select("id, updated_at").eq("user_id", user.id).maybeSingle(),
        supabase.from("microsoft_tokens").select("id, updated_at").eq("user_id", user.id).maybeSingle(),
        supabase.from("dropbox_tokens").select("id, updated_at").eq("user_id", user.id).maybeSingle(),
        supabase.from("crm_webhooks").select("id, name, webhook_url, status").eq("user_id", user.id),
      ]);

      const next: IntegrationRow[] = CATALOG.map((item) => {
        const base: IntegrationRow = { ...item, connected: false };

        if (item.key === "google_sheets" && google.data) {
          return { ...base, connected: true, detail: "Google account linked", disconnect: { table: "google_tokens", id: google.data.id } };
        }
        if (item.key === "excel_online" && microsoft.data) {
          return { ...base, connected: true, detail: "Microsoft account linked", disconnect: { table: "microsoft_tokens", id: microsoft.data.id } };
        }
        if (item.key === "dropbox" && dropbox.data) {
          return { ...base, connected: true, detail: "Dropbox account linked", disconnect: { table: "dropbox_tokens", id: dropbox.data.id } };
        }

        const match = (crm.data ?? []).find((c) => (c.crm_type ?? "").toLowerCase() === item.key);
        if (match) {
          return {
            ...base,
            name: match.name || base.name,
            connected: match.status === "active",
            detail: match.last_sync_at ? `Last sync ${new Date(match.last_sync_at).toLocaleString()}` : undefined,
            disconnect: { table: "crm_integrations", id: match.id },
          };
        }
        return base;
      });

      // Any CRM connection that isn't in the catalog (custom sources)
      for (const c of crm.data ?? []) {
        if (CATALOG.some((i) => i.key === (c.crm_type ?? "").toLowerCase())) continue;
        next.push({
          key: `crm-${c.id}`,
          name: c.name || c.crm_type,
          category: "CRM",
          icon: Link2,
          route: "/data",
          connected: c.status === "active",
          detail: c.last_sync_at ? `Last sync ${new Date(c.last_sync_at).toLocaleString()}` : undefined,
          disconnect: { table: "crm_integrations", id: c.id },
        });
      }

      for (const w of webhooks.data ?? []) {
        next.push({
          key: `hook-${w.id}`,
          name: w.name,
          category: "Webhooks",
          icon: Webhook,
          route: "/data",
          connected: w.status === "active",
          detail: w.webhook_url,
          disconnect: { table: "crm_webhooks", id: w.id },
        });
      }

      setRows(next);
    } catch (error) {
      console.error("Failed to load integrations:", error);
      toast.error("Could not load your integrations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDisconnect = async (row: IntegrationRow) => {
    if (!row.disconnect) return;
    setBusyKey(row.key);
    try {
      const { error } = await supabase.from(row.disconnect.table).delete().eq("id", row.disconnect.id);
      if (error) throw error;
      toast.success(`${row.name} disconnected`);
      await load();
    } catch (err: any) {
      toast.error(`Could not disconnect ${row.name}: ${err.message}`);
    } finally {
      setBusyKey(null);
    }
  };

  const connectedCount = rows.filter((r) => r.connected).length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Link2 className="h-5 w-5 text-primary" />
            Integration Management
          </CardTitle>
          <CardDescription>
            Every external service linked to this account, in one place.
            {!loading && ` ${connectedCount} connected.`}
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          CATEGORY_ORDER.map((category) => {
            const items = rows.filter((r) => r.category === category);
            if (!items.length) return null;
            return (
              <div key={category} className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{category}</p>
                {items.map((row) => (
                  <div key={row.key} className="flex items-center justify-between gap-4 rounded-lg border p-4">
                    <div className="flex min-w-0 items-center gap-3">
                      <row.icon className="h-5 w-5 shrink-0 text-primary" />
                      <div className="min-w-0">
                        <p className="truncate font-medium">{row.name}</p>
                        <div className="flex items-center gap-2">
                          <Badge variant={row.connected ? "default" : "secondary"}>
                            {row.connected ? "Connected" : "Not connected"}
                          </Badge>
                          {row.detail && (
                            <span className="truncate text-xs text-muted-foreground">{row.detail}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {row.connected && row.disconnect ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDisconnect(row)}
                        disabled={busyKey === row.key}
                      >
                        {busyKey === row.key
                          ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          : <Unlink className="mr-2 h-4 w-4" />}
                        Disconnect
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => navigate(row.route)}>
                        <Link2 className="mr-2 h-4 w-4" />
                        Connect
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};
